import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import MentionInput from '../shared/MentionInput';
import renderCommentContent from '../../utils/renderCommentContent';

const CommentModal = ({
  show, activePostForComment, activeItemType, commentText, setCommentText,
  commentMentions = [], setCommentMentions,
  postComments, madActionComments, posts, user,
  editingComment, editCommentText, setEditCommentText,
  onClose, onSubmit, onEditComment, onSaveEditComment, onCancelEdit,
  onDeleteConfirm, actionLoading
}) => {
  if (!show || !activePostForComment) return null;

  const comments = activeItemType === 'mad_action'
    ? (madActionComments[activePostForComment] || [])
    : (postComments[activePostForComment] || posts.find(p => p.id === activePostForComment)?.comments || []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000
          }}
        >
          <motion.div
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderTopLeftRadius: 24, borderTopRightRadius: 24,
              width: '100%', maxWidth: 500, maxHeight: '70vh',
              display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h3 data-testid="comment-modal-title" style={{ fontSize: 18, fontWeight: 700, color: '#2C3E50', margin: 0 }}>Comments</h3>
              <button onClick={onClose} data-testid="close-comment-modal"
                style={{ background: 'none', border: 'none', fontSize: 24, color: '#9CA3AF', cursor: 'pointer' }}>
                x
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {comments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
                  <MessageCircle size={48} strokeWidth={1} />
                  <p style={{ marginTop: 12 }}>No comments yet. Be the first!</p>
                </div>
              ) : (
                comments.map((comment, idx) => (
                  <div key={comment.id || idx} style={{
                    display: 'flex', gap: 12, marginBottom: 16, padding: '12px',
                    background: '#F9FAFB', borderRadius: 12
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', background: '#52B8AC',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 600, fontSize: 14, flexShrink: 0
                    }}>
                      {(comment.user_name || comment.username || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#2C3E50', fontSize: 14 }}>
                            {comment.user_name || comment.username || 'User'}
                          </span>
                          <span style={{ color: '#9CA3AF', fontSize: 12 }}>
                            {comment.created_at ? new Date(comment.created_at).toLocaleDateString() : 'Just now'}
                          </span>
                        </div>
                        {(comment.user_id === user?.id || comment.user_name === user?.username || comment.username === user?.username) && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => onEditComment(comment)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6B7280', fontSize: 12 }}>
                              Edit
                            </button>
                            <button onClick={() => onDeleteConfirm({ type: 'comment', id: comment.id, postId: activePostForComment })}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#EF4444', fontSize: 12 }}>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                      {editingComment === comment.id ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="text" value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            style={{ flex: 1, padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14 }} />
                          <button onClick={() => onSaveEditComment(comment.id)}
                            style={{ padding: '8px 12px', background: '#52B8AC', color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Save
                          </button>
                          <button onClick={onCancelEdit}
                            style={{ padding: '8px 12px', background: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <p style={{ margin: 0, color: '#374151', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                          {renderCommentContent(comment.content, comment.mentions)}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <MentionInput
                  value={commentText}
                  mentions={commentMentions}
                  onChange={(text, ms) => { setCommentText(text); setCommentMentions?.(ms); }}
                  placeholder="Add a comment... type @ to mention"
                  rows={2}
                  testId="comment-input"
                  style={{ borderRadius: 16 }}
                />
              </div>
              <button onClick={onSubmit} disabled={!commentText.trim() || actionLoading?.comment}
                data-testid="submit-comment-btn"
                style={{
                  padding: '12px 20px',
                  background: commentText.trim() ? 'linear-gradient(135deg, #52B8AC 0%, #3A8A80 100%)' : '#E5E7EB',
                  color: commentText.trim() ? 'white' : '#9CA3AF',
                  border: 'none', borderRadius: 16, fontWeight: 600,
                  cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                  opacity: actionLoading?.comment ? 0.5 : 1
                }}>
                {actionLoading?.comment ? '...' : 'Send'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommentModal;
